import React, { useCallback, useEffect, useRef, useState } from "react";
import SearchResult from "../types/SearchResult";
import ProblemDetails from "../types/responses/ProblemDetails";
import Result from "../types/Result";
import { debounce } from "../common";
import Swal from "sweetalert2";

type SearchDropdownHandlerProps<TSearchType> = {
    performSearch: (input: string) => Promise<Result<TSearchType[], ProblemDetails>>;
    toSearchResult: (result: TSearchType) => SearchResult<TSearchType>;
    searchErrorText: string;
    onSearchResultClick: (result: SearchResult<TSearchType>) => void;
};

export type SearchDropdownManager<TSearchType> = {
    searchInput: string;
    searchResults: SearchResult<TSearchType>[];
    searchResultDropdownIsOpen: boolean;
    searchSectionRef: React.RefObject<HTMLDivElement>;
    searchInputChangeHandler: (input: string) => void;
    searchResultClickHandler: (result: SearchResult<TSearchType>) => void;
};

function useSearchDropdown<TSearchType>({
    searchErrorText,
    performSearch,
    toSearchResult,
    onSearchResultClick
}: SearchDropdownHandlerProps<TSearchType>): SearchDropdownManager<TSearchType> {
    const [searchInput, setSearchInput] = useState("");
    const [searchResults, setSearchResults] = useState<SearchResult<TSearchType>[]>([]);
    const [searchResultDropdownIsOpen, setSearchResultDropdownIsOpen] = useState(false);

    const searchSectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function clickHandler(e: MouseEvent) {
            if (!searchSectionRef.current || !searchSectionRef.current.contains(e.target as Node)) {
                setSearchResultDropdownIsOpen(false);
            }
        }

        document.addEventListener("mousedown", clickHandler);
        return () => document.removeEventListener("mousedown", clickHandler);
    }, []);

    const debounceSearch = useCallback(
        debounce(async (input: string[]) => {
            if (input.length === 0 || !input.some((str) => str && str.length > 0)) {
                setSearchResultDropdownIsOpen(false);
                return;
            }

            const inputString: string = input[0];

            const result: Result<TSearchType[], ProblemDetails> = await performSearch(inputString);

            if (result.failed) {
                console.error(result.error);
                setSearchResultDropdownIsOpen(false);
                Swal.fire({
                    icon: "error",
                    text: searchErrorText
                });

                return;
            }

            const searchResults = result.value.map(toSearchResult);

            setSearchResults(searchResults);
            setSearchResultDropdownIsOpen(true);
        }, 300),
        []
    );

    function searchInputChangeHandler(newValue: string) {
        setSearchInput(newValue);
        debounceSearch(newValue);
    }

    function searchResultClickHandler(result: SearchResult<TSearchType>) {
        setSearchResultDropdownIsOpen(false);
        setSearchInput(result.text);
        onSearchResultClick(result);
    }

    return {
        searchInput,
        searchResults,
        searchResultDropdownIsOpen,
        searchSectionRef,
        searchResultClickHandler,
        searchInputChangeHandler,
    }

}

export default useSearchDropdown;
